import { HttpClient } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MAT_SELECT_CONFIG } from '@angular/material/select';
import { MatDrawer } from '@angular/material/sidenav';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BehaviorSubject, debounceTime, Observable, Subscription, take } from 'rxjs';
import {
  AutoUnsubscribe,
  LANGUAGE_BY_DEFAULT,
  Language,
  PERMISSION_CONFIG,
  PermissionConfig,
  Permission,
  getSortedLanguageList,
  IconNames,
} from '../../../common';
import {
  CONTACT_EMAIL,
  CONTACT_PHONE_NUMBER,
  RAISE_TICKET_URL,
  USER_GUIDE,
} from '../../../common/constants/contact-info';
import { AppFacade, AuthUserData } from '../../../store';
import { AnnouncementsFacade } from '../../../store/state/announcements/announcements.facade';
import { AuthFacade } from '../../../store/state/auth/auth.facade';
import { ProjectorName } from '../../projection/projector.constant';
import { CustomSnackBarComponent } from '../../custom-snackbar/custom-snackbar.component';
import { map } from 'rxjs/operators';

@Component({
    selector: 'container-dashboard-header',
    templateUrl: './dashboard-header.component.html',
    styleUrls: ['./dashboard-header.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: MAT_SELECT_CONFIG,
            useValue: { overlayPanelClass: 'language-panel' },
        },
    ],
    standalone: false
})
@AutoUnsubscribe
export class HeaderComponent implements OnInit {
  @Input() userData: AuthUserData;
  @Input() sideNavDrawer: BehaviorSubject<MatDrawer>;
  @Output() logout = new EventEmitter<void>();
  @Output() changePassword = new EventEmitter<void>();
  @Output() toggleMenu = new EventEmitter<void>();

  language = Language;
  languageControl = new FormControl<Language>(LANGUAGE_BY_DEFAULT);
  languageList$: Observable<Language[]> = this.authFacade.defaultLanguage$.pipe(
    map((language: Language) => getSortedLanguageList(language)),
  );
  subscriptions: Subscription[] = [];
  projectorName = ProjectorName;
  contactEmail = CONTACT_EMAIL;
  contactPhoneNumber = CONTACT_PHONE_NUMBER;
  userGuide = USER_GUIDE;
  raiseTicketUrl = RAISE_TICKET_URL;
  permissionConfig: PermissionConfig = PERMISSION_CONFIG;
  isSkidUser = false;
  showAnnouncementOnce = false;
  isCanaryTransportPlanner$: Observable<boolean> = this.authFacade.hasPermission$(
    PERMISSION_CONFIG[Permission.TrackAndTrace][Permission.CanaryTransportLevel][Permission.Self],
  );

  textWithoutLinks: string;
  extractedLinks: string[];
  linkText: string[];
  iconNames = IconNames;

  constructor(
    public authFacade: AuthFacade,
    public http: HttpClient,
    private appFacade: AppFacade,
    private cd: ChangeDetectorRef,
    private snackBar: MatSnackBar,
    private announcementFacade: AnnouncementsFacade,
  ) {}

  ngOnInit(): void {
    this.subscriptions.push(
      this.announcementFacade.announcements$.pipe(debounceTime(300), take(1)).subscribe((content: string) => {
        if (content && !this.showAnnouncementOnce) {
          this.textWithoutLinks = this.removeTags(content, 'Link');
          this.textWithoutLinks = this.removeTags(this.textWithoutLinks, 'LinkText');

          this.linkText = this.extractTags(content, 'LinkText');
          this.extractedLinks = this.extractTags(content, 'Link');

          this.snackBar.openFromComponent(CustomSnackBarComponent, {
            data: {
              message: this.textWithoutLinks,
              link: this.extractedLinks,
              linkText: this.linkText,
              preClose: () => {
                this.snackBar.dismiss();
              },
            },
            duration: 0,
            verticalPosition: 'top',
            horizontalPosition: 'center',
            panelClass: ['custom-snackbar'],
          });
          this.showAnnouncementOnce = true;
        }
      }),
      this.sideNavDrawer.subscribe(() => {
        this.cd.markForCheck();
      }),

      this.languageControl.valueChanges.subscribe((language: Language) => this.appFacade.changeLang(language)),

      this.authFacade.defaultLanguage$
        .pipe(map((language: Language) => this.languageControl.setValue(language, { emitEvent: false })))
        .subscribe(),
    );
    this.isSkidUser = !!this.authFacade.getSkidUrl();
  }

  openSkidLogIn(): void {
    window.open(`${this.authFacade.getSkidUrl()}/myprofile`, '_blank');
  }

  private removeTags(text: string, tag: string): string {
    const regex = new RegExp(`<${tag}>.*?<\\/${tag}>`, 'g');
    return text.replace(regex, '').replace(/\s+/g, ' ').trim();
  }

  private extractTags(text: string, tag: string): string[] {
    const regex = new RegExp(`<${tag}>(.*?)<\\/${tag}>`, 'g');
    const content = [];
    let match;
    while ((match = regex.exec(text)) !== null) {
      content.push(match[1]);
    }
    return content;
  }
}
